import {
  isTombstonableSourceFile,
  type DesignerSourceFile,
} from '@packages/domains/code-libraries/data-types';

import {SANDBOX_IGNORED_EXTENSIONS} from '../constants';
import type {SourceFilesState} from '../flux/sourceFilesState';
import {ensureLeadingSlash} from '../paths';
import {classifySourceFile} from './components';
import {parseCmsSnapshotCollectionId} from './cmsSnapshot';
import {isProjectedFile} from './isProjectedFile';
import {
  collectInvisibleKeys,
  scopeToComponent,
  type ComponentSource,
} from './sourceFileImports';

/**
 * Files a single automatic pass may retire. Above it, collect nothing: a pass
 * that wants to delete this many files is evidence the visibility computation
 * is wrong, not evidence of that many unused files.
 */
export const MAX_SOURCE_FILES_PER_GC_PASS = 5;

/**
 * Reference forms `parseImportSpecifiers` cannot resolve, so a file reached
 * only through one of them reads as unimported.
 *
 * The whole pass is skipped when any of them appears, rather than guessing per
 * file: the collector deletes, so an unresolvable reference has to mean "do not
 * decide" and not "nothing imports this". Scanned over source files as well as
 * components, because the module doing this is usually a barrel.
 *
 * `import.meta.glob` names its targets by pattern; a template literal or a
 * concatenation puts them behind a runtime value; `require` and
 * `import.meta.url` are different syntax entirely.
 */
const UNANALYZABLE_REFERENCES = [
  'import.meta.glob',
  'import.meta.url',
  /\brequire(?:\s|\/\*[\s\S]*?\*\/|\/\/[^\n]*(?:\n|$))*\(/,
  // CSS escapes need token decoding before their path can be resolved.
  /url(?:\s|\/\*[\s\S]*?\*\/)*\([^)]*\\/i,
  // A dynamic import whose argument is anything but one closed quoted string:
  // a template literal, a concatenation (`'./x/' + n`), or a comment sitting
  // between the paren and the quote.
  /import(?:\s|\/\*[\s\S]*?\*\/|\/\/[^\n]*(?:\n|$))*\(\s*(?!(['"])[^'"\n]*\1\s*\))/,
];

const hasUnanalyzableReference = (text: string | undefined): boolean => {
  // `code` and `content` are typed as strings, but reification copies a
  // component's `source` verbatim, so a malformed record can carry none.
  if (typeof text !== 'string') return true;
  return UNANALYZABLE_REFERENCES.some((form) =>
    typeof form === 'string' ? text.includes(form) : form.test(text)
  );
};

export const hasUnanalyzableSourceFileReferences = (
  componentSources: readonly ComponentSource[],
  files: SourceFilesState
): boolean =>
  componentSources.some(({code}) => hasUnanalyzableReference(code)) ||
  // A nested package manifest can redirect a directory import through exports,
  // browser, module, or main -- including outside its folder or into a function
  // file that folder widening deliberately excludes. Refuse GC instead of
  // reimplementing Vite's package resolver incompletely.
  Object.values(files).some(({name}) => name.endsWith('/package.json')) ||
  Object.values(files).some(({content}) => hasUnanalyzableReference(content));

/**
 * "Invisible in the tree" does not mean "dead" for files the platform manages,
 * so eligibility is an explicit set of exclusions rather than something the
 * import graph implies.
 */
export const isCollectableSourceFile = (file: DesignerSourceFile): boolean => {
  if (!isTombstonableSourceFile(file)) return false;
  // `isProjectedFile` gates the mint path only -- hydration and catchup insert
  // server rows verbatim, so a stale `.webflow.tsx` row can be sitting here.
  if (isProjectedFile(ensureLeadingSlash(file.name))) return false;
  if (SANDBOX_IGNORED_EXTENSIONS.some((ext) => file.name.endsWith(ext))) {
    return false;
  }
  // A component references its CMS snapshot by substring, not by import, so the
  // graph always reads one as unused.
  const leaf = file.name.slice(file.name.lastIndexOf('/') + 1);
  if (parseCmsSnapshotCollectionId(leaf) !== null) return false;
  // The root is never a widening folder, so every unimported root module reads
  // as invisible -- including a hand-written `types.ts` someone left unimported
  // on purpose. Functions never rely on folder visibility, so they still go.
  const isAtRoot = file.name.lastIndexOf('/') <= 0;
  return !isAtRoot || classifySourceFile(file.name) === 'function';
};

export type ComponentRemovalCandidatesResult =
  | {status: 'collected'; keys: string[]}
  | {status: 'refused'; reason: 'missing_component' | 'unanalyzable'}
  | {status: 'refused'; reason: 'over_cap'; candidates: number};

export const collectComponentRemovalCandidates = (
  componentSourcesBefore: readonly ComponentSource[],
  deletedComponentId: string,
  files: SourceFilesState,
  protectedKeys: ReadonlySet<string> = new Set()
): ComponentRemovalCandidatesResult => {
  const deletedSource = componentSourcesBefore.find(
    ({id}) => id === deletedComponentId
  );
  if (deletedSource === undefined) {
    return {status: 'refused', reason: 'missing_component'};
  }
  if (hasUnanalyzableSourceFileReferences(componentSourcesBefore, files)) {
    return {status: 'refused', reason: 'unanalyzable'};
  }

  const deletedScope = scopeToComponent(deletedSource.code, files);
  const remainingSources = componentSourcesBefore.filter(
    ({id}) => id !== deletedComponentId
  );
  const remainingInvisible = collectInvisibleKeys(
    remainingSources,
    files,
    protectedKeys
  );
  const keys = Object.keys(deletedScope).filter((key) => {
    const sourceFile = files[key];
    return (
      sourceFile !== undefined &&
      !protectedKeys.has(key) &&
      remainingInvisible.has(key) &&
      isCollectableSourceFile(sourceFile)
    );
  });

  if (keys.length > MAX_SOURCE_FILES_PER_GC_PASS) {
    return {status: 'refused', reason: 'over_cap', candidates: keys.length};
  }
  return {status: 'collected', keys};
};
